import {DateTime} from 'luxon';

const FRIDAY_WEEKEND_START = 16; // Weekend starts at friday 17:00, but hours go from 0 to 23
/** These are Luxon values */
const FRIDAY = 5;
const SATURDAY = 6;
const SUNDAY = 7;

/**
 * Returns a non JS Date for the given ISO string date. If invalid, it will return null
 * @param {string} date date string in ISO format
 * @returns new date (not JS Date) for the string if it's valid date
 */
const getDateFromISO = (date) => {
  const newDate = DateTime.fromISO(date, {setZone: true});

  return newDate.isValid ? newDate : null;
};

/**
 * Obtains today date in the given timezone
 * @param {string} timezone the string representation of the timezone
 * @returns today value transformed to the given timezone
 */
const getTodayInTimezone = (timezone) => {
  const today = DateTime.now().setZone(timezone || null, {setZone: true});

  return today.isValid ? today : null;
};

/**
 * Converts the given date to Luxon DateTime object.
 * @param {string | Date | DateTime} date the date to transform to Luxon DateTime
 * @returns Luxon DateTime object for the given date
 */
const getLuxonDate = (date) => {
  if (typeof date === 'string') {
    return DateTime.fromISO(date, {});
  } else if (date instanceof Date) {
    return DateTime.fromJSDate(date);
  }

  return date;
};

/**
 * Adds the given number of days to the date
 * @param {string | Date | DateTime} date the date to which we want to add days
 * @param {number} days the number of days
 * @returns the date with the days added
 */
const addDays = (date, days) => getLuxonDate(date).plus({days});

/**
 * Adds the given number of weeks to the date
 * @param {string | Date | DateTime} date the date to which we want to add weeks
 * @param {weeks} weeks the number of weeks
 * @returns the date with the weeks added
 */
const addWeeks = (date, weeks) => getLuxonDate(date).plus({weeks});

/**
 * Checks if two dates are the same day
 * @param {string | Date | DateTime} date1 first date to compare
 * @param {string | Date | DateTime} date2 second date to compare
 * @returns true if both dates have same day, month and year
 */
const isSameDay = (date1, date2) => {
  if (!date1 || !date2) {
    return false;
  }

  const date1Luxon = getLuxonDate(date1);
  const date2Luxon = getLuxonDate(date2);

  return (
    date1Luxon.isValid &&
    date2Luxon.isValid &&
    date1Luxon.hasSame(date2Luxon, 'day') &&
    date1Luxon.hasSame(date2Luxon, 'month') &&
    date1Luxon.hasSame(date2Luxon, 'year')
  );
};

/**
 * Checks if two dates are in the same week
 * @param {string | Date | DateTime} date1 first date to compare
 * @param {string | Date | DateTime} date2 second date to compare
 * @returns true if both dates are in the same week (Mon to Sun)
 */
const isSameWeek = (date1, date2) => {
  const date1Luxon = getLuxonDate(date1);
  const date2Luxon = getLuxonDate(date2);

  return date1Luxon.startOf('week') <= date2Luxon && date1Luxon.endOf('week') >= date2Luxon;
};

/**
 * Checks if the given date is considered weekend. For this, we will consider weekend from Friday at 17:00 to Sunday midnight
 * @param {string | Date | DateTime} date the date to check
 * @returns true if the date is in weekend
 */
const isWeekend = (date) => {
  const dateLuxon = getLuxonDate(date);

  return (
    (dateLuxon.weekday === FRIDAY && dateLuxon.hour >= FRIDAY_WEEKEND_START) ||
    dateLuxon.weekday === SATURDAY ||
    dateLuxon.weekday === SUNDAY
  );
};

/**
 * If both dates are valid dates, it returns the difference in days between them
 * @param {string | Date | DateTime} date1 the first date to compare
 * @param {string | Date | DateTime} date2 the second date to compare
 * @returns the difference in days between the dates
 */
const diffInDays = (date1, date2) => {
  if (!date1 || !date2) {
    return null;
  }

  const date1Luxon = getLuxonDate(date1);
  const date2Luxon = getLuxonDate(date2);
  if (!date1Luxon.isValid || !date2Luxon.isValid) {
    return null;
  }

  return date1Luxon.diff(date2Luxon, 'days').days;
};

export {getDateFromISO, getTodayInTimezone, addDays, addWeeks, isSameDay, isSameWeek, isWeekend, diffInDays};
